import * as React from 'react';
import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
  useRecurly
} from '@recurly/react-recurly';
import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonSpinner
} from '@ionic/react';
import './CreditCardForm.scss';
import CreditCard from 'assets/icons/credit-card.svg';
import ErrorIcon from 'assets/icons/overdue.svg';
import { RootState } from 'core/store';
import { useDispatch, useSelector } from 'react-redux';
import { setSignupLoading } from 'features/shared/signup/SignupSlice';
import { useHistory, useLocation } from 'react-router';
import { displayError } from 'core/services/AlertActions';
import { COUNTRY_LIST, HKBillingPeriod, HKPlatform } from 'core/constants';
import {
  hideSubscriptionSummaryModal,
  setBillingAddress1,
  setBillingCity,
  setBillingCountry,
  setBillingFirstName,
  setBillingLastName,
  setBillingPostalCode,
  setBillingState,
  setNewCreditCard,
  setShowCreditCardForm
} from 'features/shared/billing/BillingSlice';
import { renewSubscription } from 'features/shared/billing/BillingActions';
import { gaBtnClickEvent, getCurrencySettings } from 'core/util';
import { useEffect, useState } from 'react';
import { createDigitalAccount } from 'features/shared/signup/SignupActions';
import {
  requestTokenForFirebaseUserSuccess,
  setAuthEmail,
  setAuthIdToken,
  setError
} from 'features/shared/login/LoginSlice';
import {
  FirebaseAuthentication,
  GetCurrentUserResult
} from '@capacitor-firebase/authentication';
import { updateUserMe } from 'core/services/UserActions';
import { Capacitor } from '@capacitor/core';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFirebaseAuth } from 'core/hooks/useFirebase';
import { getApp } from 'firebase/app';
import { cs } from 'date-fns/locale';

// IonInput/IonSelect's `label` prop only accepts a plain string, so a
// colored asterisk has to go through the `label` slot instead (a direct
// child with slot="label") rather than the `label` prop.
const RequiredLabel: React.FC<{ text: string; filled: boolean }> = ({
  text,
  filled
}) => (
  <>
    {text}
    {!filled && <span className="hk-checkout-required-asterisk"> *</span>}
  </>
);

/**
 * The CreditCardForm component is a form that the user can use to enter their
 * credit card information. It uses the Recurly library to validate the
 * information and create a billing token. The form is used on the
 * /signup/recurly and /renew/checkout pages.
 *
 */
const CreditCardForm: React.FC = () => {
  const {
    firstName,
    lastName,
    email,
    password,
    uid,
    address1,
    city,
    state,
    postal_code,
    access_token,
    zipcodeError,
    malformedAddressError
  } = useSelector((state: RootState) => state.signup);
  const { loading } = useSelector((state: RootState) => state.loading);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const { currentHome } = useSelector((state: RootState) => state.home);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const {
    newAddOns,
    newPlan,
    newBillingPeriod,
    promoCode,
    autoRenew,
    error,
    billingAddress1,
    billingCity,
    billingCountry,
    billingFirstName,
    billingLastName,
    billingPostalCode,
    billingState
  } = useSelector((state: RootState) => state.billing);
  const { loggedIn } = useSelector((state: RootState) => state.login);
  const formRef = React.useRef<HTMLFormElement>(null);
  const recurly = useRecurly();
  const [cardValid, setCardValid] = useState({
    number: false,
    month: false,
    year: false,
    cvv: false
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // One ref per required field, grouped so focusFirstInvalidField() can walk
  // them in visual order without a separate named const per field.
  const fieldRefs = {
    firstName: React.useRef<HTMLIonInputElement>(null),
    lastName: React.useRef<HTMLIonInputElement>(null),
    address1: React.useRef<HTMLIonInputElement>(null),
    city: React.useRef<HTMLIonInputElement>(null),
    state: React.useRef<HTMLIonInputElement>(null),
    postalCode: React.useRef<HTMLIonInputElement>(null),
    country: React.useRef<HTMLIonSelectElement>(null),
    cardSection: React.useRef<HTMLDivElement>(null)
  };
  // Shared by checkoutDisabled(), focusFirstInvalidField(), and
  // getDisabledReasonMessage() so the three stay in sync on what "invalid"
  // means.
  const isBillingAddressValid = !!(
    billingAddress1 &&
    billingCity &&
    billingCountry &&
    billingFirstName &&
    billingLastName &&
    billingPostalCode &&
    billingState
  );
  const isCardInfoValid = Object.values(cardValid).every(Boolean);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { recurlyCurrency, isCanadian: isCanadianBilling } =
    getCurrencySettings(currentHome?.country, billingCountry);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    gaBtnClickEvent('submit_payment');
    event.preventDefault();
    setSubmitAttempted(true);

    if (loggedIn) {
      if (formRef !== null && formRef.current !== null) {
        recurly.token(formRef.current, async (err, token) => {
          if (err) {
            console.log(err);
            dispatch(displayError(err.message));
          } else {
            const cardInfo: any = {
              billing_token_id: token.id
            };
            const body: any = {
              currency: recurlyCurrency,
              base_plan_id: newPlan.id,
              addon_ids: newAddOns,
              billing_period:
                newBillingPeriod === HKBillingPeriod.MONTHLY ? 1 : 12,
              billing_token_id: token.id,
              auto_renew: autoRenew
            };
            if (promoCode) {
              body['coupon_code'] = promoCode;
            }
            dispatch(setNewCreditCard(cardInfo));
            if (!!homeId) {
              await dispatch(renewSubscription(homeId, body));
              if (!error) {
                dispatch(hideSubscriptionSummaryModal());
                setTimeout(() => {
                  history.push('/renew/confirmation');
                }, 0);
              } else {
                console.log(error);
                dispatch(displayError(error));
              }
            }
          }
        });
      }
    } else {
      gaBtnClickEvent('submit_sign_up');
      event.preventDefault();
      if (formRef !== null && formRef.current !== null) {
        recurly.token(formRef.current, (err, token) => {
          if (err) {
            console.log(err);
            if (err.message != null) {
              dispatch(displayError(err.message));
            } else {
              dispatch(displayError('Could not use address to create account'));
            }
          } else {
            const body: any = {
              email: email,
              first_name: firstName,
              last_name: lastName,
              postal_code: postal_code,
              address_1: address1,
              address_2: null,
              city: city,
              state: state,
              currency: recurlyCurrency,
              base_plan_id: newPlan.id,
              base_plan_type_id: newPlan.type_id,
              addon_ids: newAddOns,
              billing_period:
                newBillingPeriod === HKBillingPeriod.MONTHLY ? 1 : 12,
              term: newBillingPeriod === HKBillingPeriod.MONTHLY ? 1 : 12,
              billing_token_id: token.id,
              auto_renew: autoRenew
            };
            if (promoCode) {
              body['coupon_code'] = promoCode;
            }
            if (!!password) {
              body['password'] = password;
            } else {
              body['uid'] = uid;
            }

            dispatch(createDigitalAccount(body));
          }
        });
      }
    }
  }

  const CompleteTermsAndConditions = () => {
    const body = { terms_and_conditions: true };
    dispatch(updateUserMe(body));
  };

  const getCurrentUser = async (): Promise<GetCurrentUserResult> => {
    const result = await FirebaseAuthentication.getCurrentUser();
    return result;
  };

  const getIdToken = async () => {
    const result = await FirebaseAuthentication.getIdToken();
    return result.token;
  };

  const getIdTokenForEmail = (email: string) => {
    dispatch(setAuthEmail(email));
    getIdToken().then((token) => {
      dispatch(setAuthIdToken(token));
    });
  };

  const signInWithPassword = async (email: string, password: string) => {
    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email: email,
      password: password
    }).catch((error) => {
      dispatch(setError('Sign in failed. Please try again.'));
    });
    if (!!result) {
      const auth = getFirebaseAuth();
      getCurrentUser().then((result) => {
        if (!!result.user) {
          getIdTokenForEmail(result.user.email!);
        }
      });
      await signInWithEmailAndPassword(auth, email, password);
    }
  };

  /**
   * Checks whether the checkout button should be disabled or not.
   *
   * @returns {boolean} True if the checkout button should be disabled.
   */
  const checkoutDisabled = () => {
    const isSignupPage = location.pathname.includes('/signup/recurly');
    const isFullServicePlan = newPlan.name.includes('Full Service');
    const isAddressInvalid = !address1;

    /**
     * If on signup page, then the checkout button should be disabled if the user has not entered a signup address.
     */
    if (isSignupPage) {
      if (isFullServicePlan) {
        return (
          zipcodeError ||
          !isBillingAddressValid ||
          !isCardInfoValid ||
          isAddressInvalid
        );
      }
      return isBillingAddressValid || !isCardInfoValid || isAddressInvalid;
    }

    /**
     * If on renew page, then the checkout button should be disabled if the user has not entered a 5 digit zip code.
     */
    return !isBillingAddressValid || !isCardInfoValid;
  };

  /**
   * Scrolls to and focuses the first empty required field, in the same
   * top-to-bottom order the fields are rendered in.
   */
  const focusFirstInvalidField = () => {
    const orderedFields: [boolean, React.RefObject<HTMLElement>][] = [
      [!billingFirstName, fieldRefs.firstName],
      [!billingLastName, fieldRefs.lastName],
      [!billingAddress1, fieldRefs.address1],
      [!billingCity, fieldRefs.city],
      [!billingState, fieldRefs.state],
      [!billingPostalCode, fieldRefs.postalCode],
      [!billingCountry, fieldRefs.country],
      [!isCardInfoValid, fieldRefs.cardSection]
    ];
    const firstInvalid = orderedFields.find(([isInvalid]) => isInvalid);
    const el = firstInvalid?.[1].current as any;
    if (!el) {
      return;
    }
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof el.setFocus === 'function') {
      el.setFocus();
    }
  };

  /**
   * The message explaining why "Checkout and Pay" is disabled, shown via the
   * desktop/mobile hint below the button. Only meaningful while
   * checkoutDisabled() is true — mirrors focusFirstInvalidField()'s priority
   * of billing fields before card details.
   */
  const getDisabledReasonMessage = () =>
    isBillingAddressValid
      ? 'Please enter valid card details before continuing'
      : 'Please complete all required fields before continuing';

  /**
   * The submit button stays a native `type="submit"` even while disabled, so
   * a disabled click never reaches `handleSubmit` — this is the only handler
   * that runs when the homeowner clicks/taps "Checkout and Pay" while it's
   * disabled, and is a no-op once the button is enabled (the form's own
   * onSubmit takes over from there).
   */
  const handleCheckoutAttempt = () => {
    setSubmitAttempted(true);
    if (checkoutDisabled()) {
      focusFirstInvalidField();
    }
  };

  // One-time default: CreditCardForm is swapped in/out (not an IonPage), so
  // Ionic view lifecycle hooks don't apply here — this seeds Country exactly
  // once per mount without overwriting an already-selected value.
  useEffect(() => {
    if (!billingCountry) {
      dispatch(setBillingCountry('US'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (access_token) {
      dispatch(
        requestTokenForFirebaseUserSuccess({ access_token: access_token })
      );
      if (!!password) {
        signInWithPassword(email, password!).then();
      }
      CompleteTermsAndConditions();
      dispatch(setSignupLoading(false));
      history.push(`/checkpoint`);
    }
  }, [dispatch, access_token]);

  function RecurlyHiddenInputs() {
    return (
      <>
        {[
          ['country', billingCountry],
          ['first_name', billingFirstName],
          ['last_name', billingLastName],
          ['address1', billingAddress1],
          ['city', billingCity],
          ['state', billingState],
          ['postal_code', billingPostalCode]
        ].map(([field, value]) => (
          <input
            key={field}
            type="hidden"
            name={field}
            data-recurly={field}
            value={value}
          />
        ))}
      </>
    );
  }

  function DesktopCreditCardFormView() {
    return (
      <>
        <form
          className="hk-desktop-recurly-form"
          onSubmit={handleSubmit}
          ref={formRef}
        >
          <div className="hk-desktop-recurly-form-container">
            <label className="hk-desktop-recurly-form-address">
              BILLING ADDRESS
            </label>
            <div className="hk-desktop-recurly-form-inputs hk-desktop-recurly-form-name-row">
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.firstName}
                  className={`hk-desktop-recurly-form__field${
                    submitAttempted && !billingFirstName
                      ? ' ion-invalid ion-touched'
                      : ''
                  }`}
                  value={billingFirstName}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingFirstName ? 'danger' : 'dark'
                  }
                  errorText={
                    submitAttempted && !billingFirstName
                      ? 'This field is required'
                      : undefined
                  }
                  onIonChange={(e: any) => {
                    dispatch(setBillingFirstName(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text="First Name"
                      filled={!!billingFirstName}
                    />
                  </div>
                </IonInput>
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.lastName}
                  className="hk-desktop-recurly-form__field"
                  value={billingLastName}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingLastName ? 'danger' : 'dark'
                  }
                  onIonChange={(e: any) => {
                    dispatch(setBillingLastName(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text="Last Name"
                      filled={!!billingLastName}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingLastName && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
            </div>
            <div className="hk-desktop-recurly-form-inputs hk-desktop-recurly-form-name-row">
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.address1}
                  className="hk-desktop-recurly-form__field"
                  value={billingAddress1}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingAddress1 ? 'danger' : 'dark'
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingAddress1(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="Address" filled={!!billingAddress1} />
                  </div>
                </IonInput>
                {submitAttempted && !billingAddress1 && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
            </div>
            <div className="hk-desktop-recurly-form-inputs hk-desktop-recurly-form-name-row">
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.city}
                  className="hk-desktop-recurly-form__field"
                  value={billingCity}
                  required
                  labelPlacement="floating"
                  color={submitAttempted && !billingCity ? 'danger' : 'dark'}
                  onIonChange={(e) => {
                    dispatch(setBillingCity(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="City" filled={!!billingCity} />
                  </div>
                </IonInput>
                {submitAttempted && !billingCity && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.state}
                  className="hk-desktop-recurly-form__field"
                  value={billingState}
                  required
                  labelPlacement="floating"
                  color={submitAttempted && !billingState ? 'danger' : 'dark'}
                  onIonChange={(e) => {
                    dispatch(setBillingState(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text={isCanadianBilling ? 'Province' : 'State'}
                      filled={!!billingState}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingState && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.postalCode}
                  className="hk-desktop-recurly-form__field"
                  value={billingPostalCode}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingPostalCode ? 'danger' : 'dark'
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingPostalCode(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text={isCanadianBilling ? 'Postal Code' : 'ZIP'}
                      filled={!!billingPostalCode}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingPostalCode && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonSelect
                  ref={fieldRefs.country}
                  className="hk-desktop-recurly-form__select"
                  labelPlacement="floating"
                  value={billingCountry}
                  interface="popover"
                  interfaceOptions={{
                    cssClass: 'select-country-popover'
                  }}
                  color={
                    submitAttempted && !billingCountry ? 'danger' : undefined
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingCountry(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="Country" filled={!!billingCountry} />
                  </div>
                  {COUNTRY_LIST.map((country) => {
                    return (
                      <IonSelectOption
                        key={country.abbreviation}
                        value={country.abbreviation}
                      >
                        {country.name}
                      </IonSelectOption>
                    );
                  })}
                </IonSelect>
                {submitAttempted && !billingCountry && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
            </div>
          </div>
          <div className="hk-desktop-recurly-form-container">
            <div
              className="hk-desktop-recurly-credit-card"
              ref={fieldRefs.cardSection}
            >
              <IonIcon icon={CreditCard} />
              <span>Credit Card</span>
            </div>

            <div className="hk-desktop-recurly-form-expiration-row hk-recurly-width">
              <CardNumberElement
                className="hk-desktop-recurly-form-card-number hk-desktop-recurly-width recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'Card number' }
                }}
                onChange={(e) =>
                  setCardValid((v) => ({ ...v, number: e.valid }))
                }
              />
              <CardMonthElement
                className="hk-desktop-recurly-form-card-month recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'MM' }
                }}
                onChange={(e) =>
                  setCardValid((v) => ({ ...v, month: e.valid }))
                }
              />
              <CardYearElement
                className="hk-desktop-recurly-form-card-year recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'YY' }
                }}
                onChange={(e) => setCardValid((v) => ({ ...v, year: e.valid }))}
              />
              <CardCvvElement
                className="hk-desktop-recurly-form-card-cvv recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'CVV' }
                }}
                onChange={(e) => setCardValid((v) => ({ ...v, cvv: e.valid }))}
              />
            </div>
            {submitAttempted && !Object.values(cardValid).every(Boolean) && (
              <span className="hk-checkout-field-error">
                This field is required
                <IonIcon src={ErrorIcon} />
              </span>
            )}
            <div className="hk-desktop-recurly-terms-and-conditions">
              After any free trial(s), credit(s), and/or discount(s), will be
              charged monthly plus applicable taxes on a recurring basis.{' '}
              <b>
                {' '}
                If you do not cancel a service during its free trial period, you
                will be charged. Your subscription will continue until you
                cancel. You can cancel at any time via your HomeKeep account or
                by contacting us. By selecting ‘Submit,’ you agree to the above
                terms.
              </b>
            </div>
          </div>

          <div>
            <div className="hk-desktop-recurly-form-actions">
              <div
                className="hk-desktop-recurly-form-back-button"
                onClick={() => dispatch(setShowCreditCardForm(false))}
              >
                Back
              </div>
              <div
                className="hk-checkout-button-wrapper"
                title={
                  checkoutDisabled() ? getDisabledReasonMessage() : undefined
                }
                role="button"
                tabIndex={0}
                onClick={handleCheckoutAttempt}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCheckoutAttempt();
                  }
                }}
              >
                <IonButton
                  className="hk-desktop-recurly-button"
                  disabled={checkoutDisabled() || loading}
                  expand="block"
                  color="primary"
                  strong={true}
                  size="default"
                  type="submit"
                >
                  {loading ? <IonSpinner name="dots" /> : 'Checkout and Pay'}
                </IonButton>
              </div>
            </div>
            {checkoutDisabled() && (
              <p className="hk-checkout-disabled-hint">
                {getDisabledReasonMessage()}
              </p>
            )}
          </div>
          <RecurlyHiddenInputs />
        </form>
      </>
    );
  }

  function MobileCreditCardFormView() {
    return (
      <>
        <form className="hk-recurly-form" onSubmit={handleSubmit} ref={formRef}>
          <div className="hk-recurly-form-container">
            <label className="hk-recurly-form-address">ADDRESS</label>
            <div className="hk-recurly-form-inputs">
              <IonItem className="hk-recurly-form__item">
                <IonInput
                  ref={fieldRefs.firstName}
                  className={`hk-recurly-form__field${
                    submitAttempted && !billingFirstName
                      ? ' ion-invalid ion-touched'
                      : ''
                  }`}
                  value={billingFirstName}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingFirstName ? 'danger' : 'dark'
                  }
                  errorText={
                    submitAttempted && !billingFirstName
                      ? 'This field is required'
                      : undefined
                  }
                  onIonChange={(e: any) => {
                    dispatch(setBillingFirstName(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text="First Name"
                      filled={!!billingFirstName}
                    />
                  </div>
                </IonInput>
              </IonItem>
              <IonItem className="hk-recurly-form__item">
                <IonInput
                  ref={fieldRefs.lastName}
                  className="hk-desktop-recurly-form__field"
                  value={billingLastName}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingLastName ? 'danger' : 'dark'
                  }
                  onIonChange={(e: any) => {
                    dispatch(setBillingLastName(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text="Last Name"
                      filled={!!billingLastName}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingLastName && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.address1}
                  className="hk-desktop-recurly-form__field"
                  value={billingAddress1}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingAddress1 ? 'danger' : 'dark'
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingAddress1(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="Address" filled={!!billingAddress1} />
                  </div>
                </IonInput>
                {submitAttempted && !billingAddress1 && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.city}
                  className="hk-desktop-recurly-form__field"
                  value={billingCity}
                  required
                  labelPlacement="floating"
                  color={submitAttempted && !billingCity ? 'danger' : 'dark'}
                  onIonChange={(e) => {
                    dispatch(setBillingCity(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="City" filled={!!billingCity} />
                  </div>
                </IonInput>
                {submitAttempted && !billingCity && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.state}
                  className="hk-desktop-recurly-form__field"
                  value={billingState}
                  required
                  labelPlacement="floating"
                  color={submitAttempted && !billingState ? 'danger' : 'dark'}
                  onIonChange={(e) => {
                    dispatch(setBillingState(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text={isCanadianBilling ? 'Province' : 'State'}
                      filled={!!billingState}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingState && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonInput
                  ref={fieldRefs.postalCode}
                  className="hk-desktop-recurly-form__field"
                  value={billingPostalCode}
                  required
                  labelPlacement="floating"
                  color={
                    submitAttempted && !billingPostalCode ? 'danger' : 'dark'
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingPostalCode(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel
                      text={isCanadianBilling ? 'Postal Code' : 'ZIP'}
                      filled={!!billingPostalCode}
                    />
                  </div>
                </IonInput>
                {submitAttempted && !billingPostalCode && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
              <IonItem className="hk-desktop-recurly-form__item">
                <IonSelect
                  ref={fieldRefs.country}
                  className="hk-desktop-recurly-form__select"
                  labelPlacement="floating"
                  value={billingCountry}
                  interface="popover"
                  interfaceOptions={{
                    cssClass: 'select-country-popover'
                  }}
                  color={
                    submitAttempted && !billingCountry ? 'danger' : undefined
                  }
                  onIonChange={(e) => {
                    dispatch(setBillingCountry(e.target.value));
                  }}
                >
                  <div slot="label" className="hk-checkout-field-label">
                    <RequiredLabel text="Country" filled={!!billingCountry} />
                  </div>
                  {COUNTRY_LIST.map((country) => {
                    return (
                      <IonSelectOption
                        key={country.abbreviation}
                        value={country.abbreviation}
                      >
                        {country.name}
                      </IonSelectOption>
                    );
                  })}
                </IonSelect>
                {submitAttempted && !billingCountry && (
                  <span className="hk-checkout-field-error">
                    This field is required
                    <IonIcon src={ErrorIcon} />
                  </span>
                )}
              </IonItem>
            </div>
          </div>
          <div className="hk-recurly-form-container">
            <div className="hk-recurly-credit-card" ref={fieldRefs.cardSection}>
              <IonIcon icon={CreditCard} />
              <span>Credit Card</span>
            </div>
            <CardNumberElement
              className="hk-recurly-form-card-number recurly-element-inline"
              style={{
                fontColor: '#1b1c1f',
                fontFamily: 'Open Sans',
                fontSize: '16px',
                placeholder: { content: 'Card number' }
              }}
              onChange={(e) => setCardValid((v) => ({ ...v, number: e.valid }))}
            />
            <div className="hk-recurly-form-expiration-row">
              <CardMonthElement
                className="hk-recurly-form-card-month recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'MM' }
                }}
                onChange={(e) =>
                  setCardValid((v) => ({ ...v, month: e.valid }))
                }
              />
              <CardYearElement
                className="hk-recurly-form-card-year recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'YY' }
                }}
                onChange={(e) => setCardValid((v) => ({ ...v, year: e.valid }))}
              />
              <CardCvvElement
                className="hk-recurly-form-card-cvv recurly-element-inline"
                style={{
                  fontColor: '#1b1c1f',
                  fontFamily: 'Open Sans',
                  fontSize: '16px',
                  placeholder: { content: 'CVV' }
                }}
                onChange={(e) => setCardValid((v) => ({ ...v, cvv: e.valid }))}
              />
            </div>
            {submitAttempted && !Object.values(cardValid).every(Boolean) && (
              <span className="hk-checkout-field-error">
                This field is required
                <IonIcon src={ErrorIcon} />
              </span>
            )}
            <div className="hk-recurly-terms-and-conditions">
              After any free trial(s), credit(s), and/or discount(s), will be
              charged monthly plus applicable taxes on a recurring basis.
              <b>
                {' '}
                If you do not cancel a service during its free trial period, you
                will be charged. Your subscription will continue until you
                cancel. You can cancel at any time via your HomeKeep account or
                by contacting us. By selecting ‘Submit,’ you agree to the above
                terms.
              </b>
            </div>
          </div>

          <div>
            <div className="hk-recurly-form-actions">
              <div
                className="hk-checkout-button-wrapper"
                role="button"
                tabIndex={0}
                onClick={handleCheckoutAttempt}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCheckoutAttempt();
                  }
                }}
              >
                <IonButton
                  className="hk-recurly-button"
                  disabled={checkoutDisabled() || loading}
                  expand="block"
                  color="primary"
                  strong={true}
                  size="default"
                  type="submit"
                >
                  {loading ? <IonSpinner name="dots" /> : 'Checkout and Pay'}
                </IonButton>
              </div>
            </div>
            {checkoutDisabled() && (
              <p className="hk-checkout-disabled-hint">
                {getDisabledReasonMessage()}
              </p>
            )}
          </div>
          <RecurlyHiddenInputs />
        </form>
      </>
    );
  }

  return (
    <>
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopCreditCardFormView()
        : MobileCreditCardFormView()}
    </>
  );
};

export default CreditCardForm;
